import { useSessionStorage } from 'hooks/useSessionStorage';
import React, { ReactNode, createContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@welbe/web-design-system';

type DefaultBoolean = { [key: string]: boolean };
type DefaultString = { [key: string]: string };

export interface ICandidate {
	activeStep: number;
	setActiveStep: React.Dispatch<React.SetStateAction<number>>;
	onNext: () => void;
	onPrevious: () => void;
	handleError: (type: 'success' | 'error', message?: string) => void;
	companySettings: DefaultBoolean;
	completeStep: number;
	setCompleteStep: React.Dispatch<React.SetStateAction<number>>;
	mode: string;
	id?: string;
	setId: React.Dispatch<React.SetStateAction<string>>;
	userData: DefaultString;
	setUserData: React.Dispatch<React.SetStateAction<DefaultString>>;
	isEditing: boolean;
}

export const CandidateContext = createContext<ICandidate>({} as ICandidate);

interface CandidateProviderProps {
	children: ReactNode;
}

type ParamsProps = {
	mode: string;
	id: string;
	step?: string;
};

export const CandidateProvider = ({ children }: CandidateProviderProps) => {
	const { toast } = useToast();
	const navigate = useNavigate();
	const { mode, id: RouterId, step } = useParams() as ParamsProps;
	const { getSessionStorage } = useSessionStorage();
	const [id, setId] = React.useState<string>(RouterId);
	const [activeStep, setActiveStep] = React.useState(0);
	const [completeStep, setCompleteStep] = React.useState<number>(0);
	const [companySettings, setCompanySettings] = React.useState<any>({
		showCostCenter: false,
		showAddressSection: false,
		showCandidateAppointmentProfile: false,
	});
	const [userData, setUserData] = React.useState<any>({
		employeeId: '',
		name: '',
		email: '',
		position: '',
	});

	const onNext = () => {
		if (activeStep === 1) return navigate(-1);

		if (completeStep < 2 && completeStep === activeStep) {
			setCompleteStep((prev) => prev + 1);
		}
		setActiveStep((prev) => prev + 1);
	};

	const onPrevious = () => {
		if (activeStep > 0) {
			setActiveStep((prev) => prev - 1);
			return;
		}

		navigate(-1);
	};

	const handleError = (type: 'success' | 'error', message?: string) => {
		const defaultMessages = {
			success: 'Datos guardados correctamente',
			error: 'Ocurrió un error al guardar los datos',
		};

		toast[type](message ?? defaultMessages[type]);
	};

	useEffect(() => {
		const { company }: any = JSON.parse(getSessionStorage('me') ?? '{}');
		const parsed = company?.companyPortalSettings?.candidates;

		if (parsed) setCompanySettings(parsed);
		if (mode === 'update') setCompleteStep(2);
		if (step === 'appointment') setActiveStep(1);

		return () => {
			setActiveStep(0);
			setCompleteStep(0);
			setCompanySettings({
				showCostCenter: false,
				showAddressSection: false,
				showCandidateAppointmentProfile: false,
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isEditing = mode === 'update' || !!id;

	return (
		<CandidateContext.Provider
			value={{
				activeStep,
				setActiveStep,
				onNext,
				onPrevious,
				handleError,
				companySettings,
				completeStep,
				setCompleteStep,
				mode,
				id,
				setId,
				userData,
				setUserData,
				isEditing,
			}}
		>
			{children}
		</CandidateContext.Provider>
	);
};
